/**
 * EN-15: SMS (A2P) opt-in for the pig run "User Notifications" step.
 *
 * The server only sends an SMS notification to a user who has opted in to
 * SMS messaging (Notifications / SMS Opt-in). Users who have not opted in
 * could still be given the SMS channel, and then silently never got a text.
 * The wizard now offers SMS only for opted-in users. Email is unchanged.
 */

/** A user option in the notification user list. */
export interface NotificationUserOption {
  /** Display text: the user's name with their opt-in status, see below. */
  label: string;
  /** The user's plain name (what is saved, never the status suffix). */
  name: string;
  value: string;
  /**
   * true / false from GET /user `a2pMessagingOptInState`. null when the API
   * did not send the field (an older API), so the opt-in state is unknown.
   */
  smsOptIn: boolean | null;
}

export const SMS_NOT_OPTED_IN_MESSAGE =
  'SMS is unavailable: this user has not opted in to SMS messaging (Notifications / SMS Opt-in).';

export const SMS_NOT_OPTED_IN_SELECTED_MESSAGE =
  'This user has not opted in to SMS messaging, so they will not receive SMS. Uncheck SMS or ask them to opt in.';

export const SMS_OPTED_IN_LABEL = 'Opted In';
export const SMS_OPTED_OUT_LABEL = 'Opted Out';

/** 'Opted In' / 'Opted Out', or '' when the API did not report opt-in. */
export function smsOptInStatusLabel(smsOptIn: boolean | null): string {
  if (smsOptIn === null) return '';
  return smsOptIn ? SMS_OPTED_IN_LABEL : SMS_OPTED_OUT_LABEL;
}

/**
 * The user's name suffixed with their SMS opt-in status, e.g.
 * "Mitch Lohnes - Opted In". Display only; the plain name is what is saved.
 */
export function withSmsOptInStatus(
  name: string,
  smsOptIn: boolean | null
): string {
  const status = smsOptInStatusLabel(smsOptIn);
  return status ? `${name} - ${status}` : name;
}

/** Maps a GET /user row to a notification user option. */
export function toNotificationUserOption(user: {
  id: string;
  name: string;
  a2pMessagingOptInState?: boolean | null;
}): NotificationUserOption {
  const state = user.a2pMessagingOptInState;
  const smsOptIn =
    state === undefined || state === null ? null : state === true;
  return {
    label: withSmsOptInStatus(user.name, smsOptIn),
    name: user.name,
    value: user.id,
    smsOptIn,
  };
}

/** Sorts notification user options by name (A-Z). */
export function sortNotificationUserOptions(
  users: NotificationUserOption[]
): NotificationUserOption[] {
  return users.sort((a, b) => a.name.localeCompare(b.name));
}

/** The opt-in state for `userId`, or null if unknown / not in the list. */
export function smsOptInForUser(
  users: readonly NotificationUserOption[],
  userId: string
): boolean | null {
  const user = users.find((u) => u.value === userId);
  return user ? user.smsOptIn : null;
}

/**
 * Running pig run Notifications dialog: whether a medium ('sms' | 'email' |
 * 'both' | 'none') sends SMS. 'none' counts as SMS because PigProgressCard
 * saves an entry with no channel as SMS (userNotificationsToPigRunNotifications).
 */
export function mediumSendsSms(medium: string | null | undefined): boolean {
  return medium === 'sms' || medium === 'both' || medium === 'none';
}

/** The medium the dialog switches to when a user who cannot use SMS is picked. */
export const NON_SMS_FALLBACK_MEDIUM = 'email';

/**
 * Whether `medium` may be saved for a user. A user who cannot use SMS
 * (see canUseSmsForUser) may only be given a medium that sends no SMS.
 */
export function canSaveMediumForUser(
  canUseSms: boolean,
  medium: string | null | undefined
): boolean {
  return canUseSms || !mediumSendsSms(medium);
}

/**
 * An existing entry that sends SMS to a user who cannot use SMS, by the same
 * rule as canUseSmsForUser (an explicit opt-out, or a user not in the list;
 * a missing opt-in field keeps the old behaviour). Shows the warning.
 */
export function isSmsBlockedForEntry(
  users: readonly NotificationUserOption[],
  userId: string,
  medium: string | null | undefined
): boolean {
  return mediumSendsSms(medium) && !canUseSmsForUser(users, userId);
}

/**
 * Whether SMS may be chosen for `userId`. Only an explicit `false` blocks SMS,
 * so an API that does not report opt-in yet keeps the old behaviour. An
 * unknown user id is treated as not opted in.
 */
export function canUseSmsForUser(
  users: readonly NotificationUserOption[],
  userId: string
): boolean {
  const user = users.find((u) => u.value === userId);
  if (!user) return false;
  return user.smsOptIn !== false;
}
